"use client";

import { type Lang, getDir, t as translate } from "@/lib/i18n";
import { detectBrowserLang, resolveBilingualLang } from "@/lib/locale";
import { createContext, useContext, useEffect, useState } from "react";

interface LangContextValue {
  lang: Lang;
  setLang: (l: Lang) => void;
  dir: "ltr" | "rtl";
  t: (key: string) => string;
  mode: string;
}

const LangContext = createContext<LangContextValue>({
  lang: "en",
  setLang: () => {},
  dir: "ltr",
  t: (k) => k,
  mode: "both",
});

export function LanguageProvider({
  children,
  defaultLang = "en",
  initialLang,
  mode = "both",
}: {
  children: React.ReactNode;
  /** Fallback when browser language is unknown (site default from admin). */
  defaultLang?: Lang;
  /** Server-resolved lang from Accept-Language (avoids flash on first paint). */
  initialLang?: Lang;
  mode?: string;
}) {
  const seed =
    mode === "en"
      ? "en"
      : mode === "ar"
        ? "ar"
        : (initialLang ?? defaultLang);

  const [lang, setLangState] = useState<Lang>(seed);

  useEffect(() => {
    if (mode === "en") {
      setLangState("en");
      return;
    }
    if (mode === "ar") {
      setLangState("ar");
      return;
    }

    const stored =
      typeof window !== "undefined"
        ? (localStorage.getItem("site_lang") as Lang | null)
        : null;

    setLangState(
      resolveBilingualLang(defaultLang, stored, detectBrowserLang(defaultLang)),
    );
  }, [defaultLang, mode]);

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = getDir(lang);
  }, [lang]);

  const setLang = (l: Lang) => {
    setLangState(l);
    if (mode === "both") {
      localStorage.setItem("site_lang", l);
    }
  };

  const dir = getDir(lang);
  const tFn = (key: string) => translate(key, lang);

  return (
    <LangContext.Provider value={{ lang, setLang, dir, t: tFn, mode }}>
      {children}
    </LangContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LangContext);
}
